import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 3rem 0 3rem;
  position: relative;
  min-height: auto;
  display: flex;
  flex-direction: column;
  background: white;
  height: calc(100vh - 64px);
  overflow-x: hidden;
  overflow-y: visible;

  @media (max-width: 968px) {
    padding: 0 1rem 0 1rem;
    height: auto;
    min-height: 100vh;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: center;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  flex: 1;
  padding: 2rem 0 8rem 0;
  overflow: visible;
  min-height: 0;

  @media (max-width: 1200px) {
    gap: 2rem;
    padding: 2rem 0 8rem 0;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.75rem;
    padding: 2.5rem 0 9rem 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }

  @media (max-width: 480px) {
    padding: 2rem 0 9rem 0;
  }
`;

const TextContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-right: 2rem;
  overflow: visible;

  @media (max-width: 1200px) {
    gap: 1.25rem;
    padding-right: 1.5rem;
  }

  @media (max-width: 968px) {
    padding-right: 0;
    text-align: center;
    gap: 1.5rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: ${props => props.$isStepTitle ? '1.75rem' : '2.5rem'};
  font-weight: 700;
  color: #036;
  line-height: 1.2;
  margin: ${props => props.$extraSpace ? '2rem 0 1.5rem 0' : '0.5rem 0 0 0'};
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 1200px) {
    font-size: ${props => props.$isStepTitle ? '1.6rem' : '2.25rem'};
    margin: ${props => props.$extraSpace ? '1.75rem 0 1.35rem 0' : '0.5rem 0 0 0'};
  }

  @media (max-width: 968px) {
    font-size: ${props => props.$isStepTitle ? '1.4rem' : '2rem'};
    margin: ${props => props.$extraSpace ? '1.5rem 0 1.25rem 0' : '0.75rem 0 0 0'};
    justify-content: center;
  }

  @media (max-width: 480px) {
    font-size: ${props => props.$isStepTitle ? '1.25rem' : '1.75rem'};
    margin: ${props => props.$extraSpace ? '1.25rem 0 1rem 0' : '0.5rem 0 0 0'};
  }
`;

const OrangeText = styled.span`
  color: var(--primary-color);
  background: linear-gradient(135deg, var(--primary-color) 0%, #ff9f20 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, #ff9f20 100%);
  color: white;
  font-size: 1.125rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.25);
  letter-spacing: 0.02em;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    font-size: 1rem;
    padding: 0.45rem 0.9rem;
  }

  @media (max-width: 968px) {
    font-size: 0.95rem;
    padding: 0.4rem 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    padding: 0.35rem 0.75rem;
  }
`;

const PillContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  margin-bottom: 1.25rem;

  @media (max-width: 968px) {
    justify-content: center;
    margin-top: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-top: 0.75rem;
    margin-bottom: 1.25rem;
  }
`;

const KnowledgePill = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
  letter-spacing: 0.03em;

  @media (max-width: 1200px) {
    font-size: 0.7rem;
    padding: 0.3rem 0.7rem;
  }

  @media (max-width: 968px) {
    font-size: 0.65rem;
    padding: 0.3rem 0.65rem;
  }

  @media (max-width: 480px) {
    font-size: 0.625rem;
    padding: 0.25rem 0.6rem;
  }
`;

const TimePill = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 16px;
  border: 1px solid rgba(107, 114, 128, 0.2);
  letter-spacing: 0.03em;

  @media (max-width: 1200px) {
    font-size: 0.7rem;
    padding: 0.3rem 0.7rem;
  }

  @media (max-width: 968px) {
    font-size: 0.65rem;
    padding: 0.3rem 0.65rem;
  }

  @media (max-width: 480px) {
    font-size: 0.625rem;
    padding: 0.25rem 0.6rem;
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
  line-height: 1.3;
  margin: 0;
  letter-spacing: -0.01em;

  @media (max-width: 1200px) {
    font-size: 1.125rem;
  }

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.125rem;
  color: var(--gray-600);
  line-height: 1.7;
  margin: 0;
  font-weight: 400;
  letter-spacing: -0.01em;

  @media (max-width: 1200px) {
    font-size: 1.05rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns === 3 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))'};
  gap: ${props => props.$columns === 3 ? '0.85rem' : '1.25rem'};
  margin-top: ${props => props.$columns === 3 ? '0.75rem' : '1.25rem'};
  overflow: visible;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled(motion.button).attrs(() => ({
  type: 'button'
}))`
  background: ${props => props.$active ? 'linear-gradient(180deg, #fff7ed 0%, #fffaf5 100%)' : '#ffffff'};
  border: 1.5px solid ${props => {
    if (props.$active) return 'var(--primary-color)';
    if (props.$featured) return 'rgba(255, 143, 0, 0.35)';
    return '#e5e7eb';
  }};
  border-radius: 22px;
  padding: ${props => props.$compact ? '1rem 0.85rem' : '1.5rem 1.25rem'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.$compact ? '0.6rem' : '1rem'};
  box-shadow: ${props => {
    if (props.$active) return '0 24px 40px rgba(255, 143, 0, 0.24)';
    if (props.$featured) return '0 12px 26px rgba(255, 143, 0, 0.12)';
    return '0 12px 26px rgba(15, 23, 42, 0.08)';
  }};
  transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease, z-index 0s, background 0.25s ease;
  cursor: pointer;
  position: relative;
  z-index: 1;
  width: 100%;
  appearance: none;
  text-decoration: none;
  color: inherit;
  outline: none;

  &:hover {
    box-shadow: ${props => {
      if (props.$active) return '0 26px 44px rgba(255, 143, 0, 0.28)';
      if (props.$featured) return '0 20px 36px rgba(255, 143, 0, 0.2)';
      return '0 18px 34px rgba(15, 23, 42, 0.12)';
    }};
    transform: translateY(-4px);
    z-index: 10;
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 143, 0, 0.35);
    outline-offset: 3px;
  }
`;

const OptionImage = styled.img`
  width: ${props => props.$compact ? '75px' : '120px'};
  height: ${props => props.$compact ? '75px' : '120px'};
  object-fit: contain;
  filter: drop-shadow(0 8px 18px rgba(17, 24, 39, 0.1));
`;

const OptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
`;

const OptionTitle = styled.span`
  font-weight: 700;
  font-size: ${props => props.$compact ? '0.875rem' : '1.1rem'};
  color: ${props => props.$active ? '#ff8f00' : '#0f172a'};
  text-align: center;
  line-height: 1.3;
`;

const OptionAccent = styled.span`
  display: block;
  width: 40px;
  height: 3px;
  border-radius: 999px;
  background: ${props => props.$active ? '#ff8f00' : '#0f172a'};
`;

const OptionSubtitle = styled.span`
  font-weight: 500;
  font-size: ${props => props.$compact ? '0.75rem' : '0.95rem'};
  color: ${props => props.$active ? '#f97316' : '#6b7280'};
  text-align: center;
  line-height: 1.4;
`;

const OptionDescriptionsWrapper = styled.div`
  margin-top: ${props => props.$columns === 3 ? '1.25rem' : '2rem'};
  display: grid;
  grid-template-columns: ${props => props.$columns === 3 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))'};
  gap: ${props => props.$columns === 3 ? '0.85rem' : '1.5rem'};

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionDescriptionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$compact ? '0.5rem' : '0.75rem'};
  padding: ${props => props.$compact ? '0.75rem 0.85rem' : '1rem 1.25rem'};
  border-radius: 18px;
  background: ${props => props.$active ? 'rgba(255, 143, 0, 0.08)' : 'transparent'};
  transition: background 0.25s ease;
`;

const OptionDescriptionLabel = styled.span`
  font-size: ${props => props.$compact ? '0.65rem' : '0.75rem'};
  font-weight: 600;
  color: ${props => props.$active ? '#ff8f00' : '#6b7280'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${props => props.$active ? 'rgba(255, 143, 0, 0.12)' : '#f3f4f6'};
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  display: inline-block;
  align-self: flex-start;
  transition: all 0.25s ease;
`;

const OptionDescriptionTitle = styled.span`
  font-size: ${props => props.$compact ? '0.9rem' : '1.05rem'};
  font-weight: 700;
  color: ${props => props.$active ? '#ff8f00' : '#0f172a'};
`;

const OptionDescriptionText = styled.p`
  margin: 0;
  font-size: ${props => props.$compact ? '0.85rem' : '0.975rem'};
  line-height: 1.6;
  color: ${props => props.$active ? '#ea580c' : '#475569'};
`;

// Feature Badges Styles
const BadgesContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem 1.25rem;
  margin-top: 1.5rem;

  @media (max-width: 1200px) {
    gap: 0.85rem 1rem;
    margin-top: 1.25rem;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 1.25rem;
  }
`;

const Badge = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.625rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 968px) {
    align-items: flex-start;
  }
`;

const BadgeIcon = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f7;
  border-radius: 50%;
  font-size: 1.5rem;
  transition: all 0.2s ease;
  margin-bottom: 0.125rem;

  ${Badge}:hover & {
    background: #ebebed;
  }

  @media (max-width: 1200px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    font-size: 1.375rem;
  }

  @media (max-width: 968px) {
    width: 42px;
    height: 42px;
    min-width: 42px;
    font-size: 1.3rem;
  }
`;

const BadgeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const BadgeTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 968px) {
    font-size: 0.875rem;
  }
`;

const BadgeDescription = styled.p`
  font-size: 0.8125rem;
  color: #6e6e73;
  margin: 0;
  line-height: 1.5;
  font-weight: 400;

  @media (max-width: 968px) {
    font-size: 0.75rem;
  }
`;

const USPSectionTitle = styled(motion.h3)`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1d1d1f;
  margin: 1.5rem 0 0 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
  position: relative;
  padding-left: 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--primary-color);
    border-radius: 2px;
  }

  @media (max-width: 968px) {
    font-size: 1rem;
    text-align: center;
    padding-left: 0;

    &::before {
      display: none;
    }
  }
`;

// Carousel Styles
const CarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  overflow: hidden;
  border-radius: 16px;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 1200px) {
    height: 310px;
  }

  @media (max-width: 968px) {
    height: 300px;
  }

  @media (max-width: 480px) {
    height: 280px;
  }
`;

const CarouselSlide = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.$objectFit === 'contain' ? '2rem' : '0'};

  @media (max-width: 968px) {
    padding: ${props => props.$objectFit === 'contain' ? '1.5rem' : '0'};
  }
`;

const CarouselImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$objectFit || 'cover'};
  object-position: ${props => props.$objectPosition || 'center center'};
  transform: ${props => {
    const scale = props.$scale ? `scale(${props.$scale})` : '';
    const translateY = props.$translateY ? `translateY(${props.$translateY})` : '';
    const transforms = [scale, translateY].filter(Boolean).join(' ');
    return transforms || 'none';
  }};
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08));
`;

const ArrowButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: white;
  border: 1px solid #e5e7eb;
  color: var(--gray-700);
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(255, 143, 0, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 968px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? 'var(--primary-color)' : '#d1d5db'};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : '#9ca3af'};
    transform: scale(1.2);
  }

  &:active {
    transform: scale(0.9);
  }
`;

// Progression Header Styles
const ProgressionHeader = styled.div`
  width: 100%;
  padding: 1rem 3rem 1.35rem 3rem;
  background: white;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.85rem 1rem 1.15rem 1rem;
    margin-top: 0.6rem;
  }
`;

const ProgressionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

// Progress Bar Styles
const ProgressBarContainer = styled.div`
  flex: 1;
  max-width: 600px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color) 0%, #ff9f20 100%);
  border-radius: 3px;
  position: absolute;
  left: 0;
  top: 0;
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: var(--gray-600);
  font-weight: 500;
`;

const ProgressStep = styled.div`
  flex: 1;
  text-align: center;
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--gray-400)'};
  transition: color 0.3s ease;
  font-weight: ${props => props.active ? '600' : '500'};

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

// Navigation Button Styles
const NavButton = styled(motion.button)`
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  border: 2px solid ${props => props.primary ? 'transparent' : '#e5e7eb'};
  background: ${props => {
    if (props.$isLastStep) return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)';
    if (props.primary) return 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff8f00 100%)';
    return 'white';
  }};
  color: ${props => props.primary ? 'white' : 'var(--gray-700)'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => {
    if (props.$isLastStep) return '0 4px 20px rgba(16, 185, 129, 0.4), 0 0 0 0 rgba(16, 185, 129, 0.5)';
    if (props.primary) return '0 4px 20px rgba(255, 107, 53, 0.4), 0 0 0 0 rgba(255, 107, 53, 0.5)';
    return '0 2px 8px rgba(0, 0, 0, 0.04)';
  }};
  min-width: 130px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  ${props => props.primary && `
    background-size: 200% 100%;
    animation: gradientShift 3s ease infinite;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      animation: shimmer 2s infinite;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 12px;
      padding: 2px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0.6;
    }
  `}

  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
    box-shadow: ${props => {
      if (props.$isLastStep) return '0 8px 30px rgba(16, 185, 129, 0.5), 0 0 20px rgba(52, 211, 153, 0.4)';
      if (props.primary) return '0 8px 30px rgba(255, 107, 53, 0.5), 0 0 20px rgba(255, 143, 0, 0.4)';
      return '0 4px 12px rgba(0, 0, 0, 0.08)';
    }};
    background: ${props => {
      if (props.$isLastStep) return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)';
      if (props.primary) return 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff8f00 100%)';
      return '#fafafa';
    }};
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    animation: none;

    &::before {
      display: none;
    }
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    min-width: 120px;
  }

  @keyframes gradientShift {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 200%;
    }
  }
`;

const poleOptions = [
  {
    id: 'springhead',
    title: 'Springhead Pole',
    subtitle: 'Best when receiving full big bags.',
    description: 'Springhead Poles are the best choice when receiving full big bags. The attachment mechanism is easy and manages tension on corner loops during handling, ensuring stable and safe logistics and stacking.',
    image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/springhead.avif',
    featured: true,
    carousel: {
      url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Springhead%201300mm%20met%20big%20bag.webp',
      alt: 'Springhead pole tensioning a big bag',
      objectFit: 'contain',
      scale: 1.7,
      translateY: '-15%'
    }
  },
  {
    id: 'flow',
    title: 'Flow Pole',
    subtitle: 'Best when filling and emptying internally.',
    description: 'Flow Poles are the best choice when internally filling and emptying big bags. The spring-assisted system provides 200kg of upward force to maintain maximum tension on loops, ensuring smooth material discharge and eliminating blockages during unloading operations.',
    image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/flow.avif',
    featured: false,
    carousel: {
      url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/flow.avif',
      alt: 'Flow pole maintaining loop tension during discharge',
      objectFit: 'contain',
      scale: 1.1,
      translateY: '-8%'
    }
  }
];

const deckOptions = [
  {
    id: 'standard',
    title: 'Standard Deck',
    subtitle: 'Our versatile foundation solution',
    description: 'Provides a stable conical foundation for big bags with an integrated latch system, enabling safe discharge directly through the deck while ensuring optimal unloading and transport.',
    image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Standard.avif',
    featured: true,
    carousel: {
      url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Standard.avif',
      alt: 'Standard Deck foundation',
      objectFit: 'contain',
      scale: 1.5,
      translateY: '-10%'
    }
  },
  {
    id: 'open-sided',
    title: 'Open-Sided Deck',
    subtitle: 'Enhanced accessibility for forklift operations',
    description: 'Features strategic side openings for seamless stacker access while maintaining robust big bag support. Streamlines warehouse operations and reduces load/unload times. Load capacity: 1 ton.',
    image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Open%20Sides.avif',
    featured: false,
    carousel: {
      url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Open%20Sides.avif',
      alt: 'Open-Sided Deck with accessibility',
      objectFit: 'contain',
      scale: 1.5,
      translateY: '-10%'
    }
  }
];

const unloadingOptions = [
  {
    id: 'frame-base',
    title: 'Frame Base',
    subtitle: 'Adaptable solution for every industry challenge',
    label: '1. Start With Frame Base',
    description: 'The foundation of your unloading system. This modular frame integrates seamlessly with the Neva system and provides the structural base for handling free to medium flow materials like granules, pellets, seeds, and regrind. The frame includes a carrier for mounting add-ons.',
    image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station.avif',
    featured: true,
    carousel: {
      url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station.avif',
      alt: 'Frame Base',
      objectFit: 'contain',
      scale: 1.5,
      translateY: '0%'
    }
  },
  {
    id: 'feet-set-s',
    title: 'Feet Set',
    subtitle: 'Choose from three customizable height options',
    label: '2. Choose Your Working Height',
    description: 'Select from three adjustable feet sets to match your exact working height: Feet Set S (880-1480mm), Feet Set M (1480-2080mm), or Feet Set L (2080-3180mm). Each set provides stable support and precise height adjustment for your production setup.',
    image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station%20-%20Feet%20Small%20-%2014-4.png',
    featured: false,
    carousel: {
      url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station%20-%20Feet%20Small%20-%2014-4.png',
      alt: 'Feet Set S',
      objectFit: 'contain',
      scale: 1.5,
      translateY: '0%'
    }
  },
  {
    id: 'vibrating-platform',
    title: 'Vibrating Platform Add-on',
    subtitle: 'Enhanced material flow solution',
    label: '3. Choose Add-on',
    description: 'Enhance your system with controlled vibration for challenging materials that bridge or stick. Perfect for cohesive powders, sticky materials, and products with irregular flow. Mounts directly to the frame carrier for consistent, reliable discharge.',
    image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Trilstation%208-7%20Supabase.avif',
    featured: false,
    carousel: {
      url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Trilstation%208-7%20Supabase.avif',
      alt: 'Vibrating Platform Add-on',
      objectFit: 'contain',
      scale: 1.5,
      translateY: '0%'
    }
  }
];

// Chapter content - each chapter has its own content and images
const chapters = [
  {
    id: 'neva-system',
    title: 'Meet the Indus Neva, again.',
    subtitle: 'Revolutionize Your Big Bag Handling',
    description: 'The Neva System transforms the way you manage bulk materials. Built on modular engineering principles, our system combines simplicity with industrial strength—giving you complete control over your storage and handling operations.',
    badges: [
      {
        icon: '📦',
        title: 'Reduce Storage by 80%',
        description: 'Safely stack up to 7 meters high with 6 tons in a tower'
      },
      {
        icon: '🔒',
        title: 'Integrated Latch',
        description: 'Unique latch makes unloading through the deck possible'
      },
      {
        icon: '💪',
        title: 'Heavy Duty',
        description: 'One system can hold up to 1500kg'
      }
    ],
    images: [
      {
        url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Springhead%201300mm%20met%20big%20bag.webp',
        alt: 'Springhead Pole with Big Bag',
        objectFit: 'contain',
        scale: 1.7,
        translateY: '-15%'
      },
      {
        url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/neva-warehouse.avif',
        alt: 'Indus Neva Warehouse Installation',
        objectFit: 'cover'
      }
    ]
  },
  {
    id: 'poles',
    title: 'Step 1. Select Your Pole',
    subtitle: '',
    description: '',
    images: [
      {
        url: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Springhead%201300mm%20met%20big%20bag.webp',
        alt: 'Springhead Pole with Big Bag',
        objectFit: 'contain',
        scale: 1.7,
        translateY: '-15%'
      }
    ]
  },
  {
    id: 'deck',
    title: 'Step 2. Configure Your Foundation Deck',
    subtitle: '',
    description: '',
    images: [
      {
        url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/decks.images/Deck%20Standard.avif',
        alt: 'Standard Deck',
        objectFit: 'contain',
        scale: 1.5,
        translateY: '-10%'
      }
    ]
  },
  {
    id: 'unloading',
    title: 'Step 3. Design Your Unloading Station',
    subtitle: '',
    description: '',
    images: [
      {
        url: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/unloadingstation/Unloading%20Station.avif',
        alt: 'Modular Unloading Station',
        objectFit: 'contain',
        scale: 1.5,
        translateY: '0%'
      }
    ]
  }
];

const MeetNevaSection = () => {
  const { setMainCategory } = useConfiguratorStore();
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [chapterDirection, setChapterDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const currentChapterData = chapters[currentChapter];
  const isPoleChapter = currentChapterData.id === 'poles';
  const isDeckChapter = currentChapterData.id === 'deck';
  const isUnloadingChapter = currentChapterData.id === 'unloading';
  const carouselImages = isPoleChapter
    ? poleOptions.map((option) => ({
      url: option.carousel?.url || option.image,
      alt: option.carousel?.alt || option.title,
      objectFit: option.carousel?.objectFit || 'contain',
      scale: option.carousel?.scale,
      translateY: option.carousel?.translateY
    }))
    : isDeckChapter
    ? deckOptions.map((option) => ({
      url: option.carousel?.url || option.image,
      alt: option.carousel?.alt || option.title,
      objectFit: option.carousel?.objectFit || 'contain',
      scale: option.carousel?.scale,
      translateY: option.carousel?.translateY
    }))
    : isUnloadingChapter
    ? unloadingOptions.map((option) => ({
      url: option.carousel?.url || option.image,
      alt: option.carousel?.alt || option.title,
      objectFit: option.carousel?.objectFit || 'contain',
      scale: option.carousel?.scale,
      translateY: option.carousel?.translateY
    }))
    : currentChapterData.images;
  const activeIndex = Math.min(currentIndex, carouselImages.length - 1);
  const currentImage = carouselImages[activeIndex] || carouselImages[0];

  const paginate = (newDirection) => {
    const newIndex = activeIndex + newDirection;
    if (newIndex >= 0 && newIndex < carouselImages.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
      setIsAutoPlaying(false); // Stop auto-play on manual navigation
    }
  };

  const goToSlide = (index) => {
    if (index === activeIndex) return;
    const newDirection = index > activeIndex ? 1 : -1;
    setDirection(newDirection);
    setCurrentIndex(index);
    setIsAutoPlaying(false); // Stop auto-play on manual navigation
  };

  const handleOptionCardClick = (index) => {
    if ((!isPoleChapter && !isDeckChapter && !isUnloadingChapter) || index === activeIndex) return;
    const newDirection = index > activeIndex ? 1 : -1;
    setDirection(newDirection);
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        // Loop back to start if at the end
        if (nextIndex >= carouselImages.length) {
          return 0;
        }
        return nextIndex;
      });
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, carouselImages.length]);

  // Reset auto-play when chapter changes
  useEffect(() => {
    setIsAutoPlaying(true);
    setCurrentIndex(0);
  }, [currentChapter]);

  // Chapter navigation
  const chapterVariants = {
    enter: (direction) => ({
      opacity: 0,
      x: direction > 0 ? 100 : -100
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction < 0 ? 100 : -100
    })
  };

  const handleNextChapter = () => {
    if (currentChapter < chapters.length - 1) {
      setChapterDirection(1);
      setCurrentChapter(currentChapter + 1);
      setCurrentIndex(0); // Reset carousel to first image
    } else {
      // Navigate to poles category when on last chapter
      setMainCategory('poles');
    }
  };

  const handlePrevChapter = () => {
    if (currentChapter > 0) {
      setChapterDirection(-1);
      setCurrentChapter(currentChapter - 1);
      setCurrentIndex(0); // Reset carousel to first image
    }
  };

  const progressPercentage = ((currentChapter + 1) / chapters.length) * 100;

  return (
    <Container>
      <ContentWrapper>
        <AnimatePresence mode="wait" custom={chapterDirection}>
          <TextContent
            key={currentChapter}
            custom={chapterDirection}
            variants={chapterVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            {currentChapter === 0 && (
              <PillContainer>
                <KnowledgePill
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
                >
                  Refreshment Course
                </KnowledgePill>
                <TimePill
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
                >
                  ⏱ 2 minutes
                </TimePill>
              </PillContainer>
            )}

            <Title
              $extraSpace={currentChapter === 3}
              $isStepTitle={currentChapterData.title.startsWith('Step')}
            >
              {currentChapterData.title.startsWith('Step') ? (
                <>
                  <StepBadge>{currentChapterData.title.split('.')[0]}</StepBadge>
                  {currentChapterData.title.split('.').slice(1).join('.')}
                </>
              ) : (
                currentChapterData.title
              )}
            </Title>

            {currentChapter === 1 && (
              <>
                <OptionsGrid>
                  {poleOptions.map((option, index) => {
                    const isActive = isPoleChapter && activeIndex === index;
                    return (
                      <OptionCard
                        key={option.id}
                        $featured={option.featured}
                        $active={isActive}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleOptionCardClick(index)}
                        aria-pressed={isActive}
                        aria-label={`Select ${option.title}`}
                      >
                        <OptionImage src={option.image} alt={option.title} />
                        <OptionDetails>
                          <OptionTitle $active={isActive}>{option.title}</OptionTitle>
                          {isActive && <OptionAccent $active={isActive} />}
                          <OptionSubtitle $active={isActive}>{option.subtitle}</OptionSubtitle>
                        </OptionDetails>
                      </OptionCard>
                    );
                  })}
                </OptionsGrid>

                <OptionDescriptionsWrapper>
                  {poleOptions.map((option, index) => {
                    const isActive = isPoleChapter && activeIndex === index;
                    return (
                      <OptionDescriptionCard key={`${option.id}-description`} $active={isActive}>
                        <OptionDescriptionTitle $active={isActive}>{option.title}</OptionDescriptionTitle>
                        <OptionDescriptionText $active={isActive}>{option.description}</OptionDescriptionText>
                      </OptionDescriptionCard>
                    );
                  })}
                </OptionDescriptionsWrapper>
              </>
            )}

            {currentChapter === 2 && (
              <>
                <OptionsGrid>
                  {deckOptions.map((option, index) => {
                    const isActive = isDeckChapter && activeIndex === index;
                    return (
                      <OptionCard
                        key={option.id}
                        $featured={option.featured}
                        $active={isActive}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleOptionCardClick(index)}
                        aria-pressed={isActive}
                        aria-label={`Select ${option.title}`}
                      >
                        <OptionImage src={option.image} alt={option.title} />
                        <OptionDetails>
                          <OptionTitle $active={isActive}>{option.title}</OptionTitle>
                          {isActive && <OptionAccent $active={isActive} />}
                          <OptionSubtitle $active={isActive}>{option.subtitle}</OptionSubtitle>
                        </OptionDetails>
                      </OptionCard>
                    );
                  })}
                </OptionsGrid>

                <OptionDescriptionsWrapper>
                  {deckOptions.map((option, index) => {
                    const isActive = isDeckChapter && activeIndex === index;
                    return (
                      <OptionDescriptionCard key={`${option.id}-description`} $active={isActive}>
                        <OptionDescriptionTitle $active={isActive}>{option.title}</OptionDescriptionTitle>
                        <OptionDescriptionText $active={isActive}>{option.description}</OptionDescriptionText>
                      </OptionDescriptionCard>
                    );
                  })}
                </OptionDescriptionsWrapper>
              </>
            )}

            {currentChapter === 3 && (
              <>
                <OptionsGrid $columns={3}>
                  {unloadingOptions.map((option, index) => {
                    const isActive = isUnloadingChapter && activeIndex === index;
                    return (
                      <OptionCard
                        key={option.id}
                        $featured={option.featured}
                        $active={isActive}
                        $compact
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleOptionCardClick(index)}
                        aria-pressed={isActive}
                        aria-label={`Select ${option.title}`}
                      >
                        <OptionImage src={option.image} alt={option.title} $compact />
                        <OptionDetails>
                          <OptionTitle $active={isActive} $compact>{option.title}</OptionTitle>
                          {isActive && <OptionAccent $active={isActive} />}
                          <OptionSubtitle $active={isActive} $compact>{option.subtitle}</OptionSubtitle>
                        </OptionDetails>
                      </OptionCard>
                    );
                  })}
                </OptionsGrid>

                <OptionDescriptionsWrapper $columns={3}>
                  {unloadingOptions.map((option, index) => {
                    const isActive = isUnloadingChapter && activeIndex === index;
                    return (
                      <OptionDescriptionCard key={`${option.id}-description`} $active={isActive} $compact>
                        <OptionDescriptionLabel $active={isActive} $compact>{option.label}</OptionDescriptionLabel>
                        <OptionDescriptionTitle $active={isActive} $compact>{option.title}</OptionDescriptionTitle>
                        <OptionDescriptionText $active={isActive} $compact>{option.description}</OptionDescriptionText>
                      </OptionDescriptionCard>
                    );
                  })}
                </OptionDescriptionsWrapper>
              </>
            )}

            {currentChapterData.subtitle && (
              <Subtitle>
                {currentChapterData.subtitle}
              </Subtitle>
            )}

            {currentChapterData.description && (
              <Description>
                {currentChapterData.description}
              </Description>
            )}

            {/* Feature Badges - Only show on first chapter */}
            {currentChapter === 0 && currentChapterData.badges && (
              <>
                <USPSectionTitle
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
                >
                  Key Benefits
                </USPSectionTitle>
                <BadgesContainer
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                {currentChapterData.badges.map((badge, index) => (
                  <Badge
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.4 + (index * 0.1),
                      ease: [0.4, 0, 0.2, 1]
                    }}
                  >
                    <BadgeIcon>{badge.icon}</BadgeIcon>
                    <BadgeContent>
                      <BadgeTitle>{badge.title}</BadgeTitle>
                      <BadgeDescription>{badge.description}</BadgeDescription>
                    </BadgeContent>
                  </Badge>
                ))}
              </BadgesContainer>
              </>
            )}
          </TextContent>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
        >
          <CarouselWrapper>
            <CarouselContainer>
              <AnimatePresence initial={false} custom={direction}>
                <CarouselSlide
                  key={activeIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={1}
                  onDragEnd={(e, { offset, velocity }) => {
                    const swipe = swipePower(offset.x, velocity.x);

                    if (swipe < -swipeConfidenceThreshold) {
                      paginate(1);
                    } else if (swipe > swipeConfidenceThreshold) {
                      paginate(-1);
                    }
                    setIsAutoPlaying(false); // Stop auto-play on drag
                  }}
                  $objectFit={currentImage.objectFit}
                >
                  <CarouselImage
                    src={currentImage.url}
                    alt={currentImage.alt}
                    $objectFit={currentImage.objectFit}
                    $objectPosition={currentImage.objectPosition}
                    $scale={currentImage.scale}
                    $translateY={currentImage.translateY}
                  />
                </CarouselSlide>
              </AnimatePresence>

              <ArrowButton
                direction="left"
                onClick={() => paginate(-1)}
                disabled={activeIndex === 0}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ‹
              </ArrowButton>

              <ArrowButton
                direction="right"
                onClick={() => paginate(1)}
                disabled={activeIndex === carouselImages.length - 1}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ›
              </ArrowButton>
            </CarouselContainer>

            <DotsContainer>
              {carouselImages.map((_, index) => (
                <Dot
                  key={index}
                  active={index === activeIndex}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </DotsContainer>
          </CarouselWrapper>
        </motion.div>
      </ContentWrapper>

      {/* Progression Header */}
      <ProgressionHeader>
        <ProgressionRow>
          {/* Previous Button */}
          <NavButton
            onClick={handlePrevChapter}
            disabled={currentChapter === 0}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Previous
          </NavButton>

          {/* Progress Bar */}
          <ProgressBarContainer>
            <ProgressBarTrack>
              <ProgressBarFill
                initial={{ width: '25%' }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
              />
            </ProgressBarTrack>
            <ProgressSteps>
              <ProgressStep active={currentChapter >= 0}>Neva System</ProgressStep>
              <ProgressStep active={currentChapter >= 1}>Poles</ProgressStep>
              <ProgressStep active={currentChapter >= 2}>Deck</ProgressStep>
              <ProgressStep active={currentChapter >= 3}>Unloading</ProgressStep>
            </ProgressSteps>
          </ProgressBarContainer>

          {/* Next Button */}
          <NavButton
            primary
            $isLastStep={currentChapter === chapters.length - 1}
            onClick={handleNextChapter}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {currentChapter === chapters.length - 1 ? 'Configure now!' : 'Next Step'}
          </NavButton>
        </ProgressionRow>
      </ProgressionHeader>
    </Container>
  );
};

export default MeetNevaSection;
